import * as destinationPackageService from "../../../../services/destinationPackage";
import * as destinationPackageDeliveryService from "../../../../services/destinationPackageDelivery";
import * as settings from "../../../../services/settings";

import { Button, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Table, notification } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { getRouteLabel, isEmpty, uniqueArray } from "../../../../utils/common";

import { Spin } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import permissions from "../../../../../configs/permission";
import { useAccessToken } from "../../../../contexts/AccessTokenContext";
import { usePermission } from "../../../../hooks/usePermission";

/** Coalesces concurrent identical list requests (e.g. React Strict Mode). */
const pendingDestinationPackageContainerByKey = new Map();
const pendingContainerListByKey = new Map();

const ModalAddPackageToDestination = ({ open, setOpen, data, fetchPackage }) => {
  const { hasPermission, isSuperuser } = usePermission();
  const [notiApi, contextHolder] = notification.useNotification();
  const { t, i18n } = useTranslation("destinationdelivery", {
    keyPrefix: "Destination_Delivery_Create.Add_Package_Modal",
  });
  const { t: tMessage } = useTranslation("destinationdelivery", {
    keyPrefix: "Destination_Delivery_Create.Modal_Message",
  });
  const { t: tBtn } = useTranslation("common");
  const { t: tOps } = useTranslation("options", { keyPrefix: "Package" });

  const { accessToken } = useAccessToken();
  const [containerData, setContainerData] = useState({ loading: false, page: 1, data: [], hasMore: true });
  const [tableData, setTableData] = useState({
    loading: false,
    datasource: [],
    selectedRowKeys: [],
    pagination: { current: 1, pageSize: 10 },
    total: 0,
  });
  const [submitting, setSubmitting] = useState(false);

  const [form] = Form.useForm();
  const paginationRef = useRef({ current: 1, pageSize: 10 });

  const openNotificationWithIcon = (type, message, description) => {
    notiApi[type]({
      message,
      description,
    });
  };

  useEffect(() => {
    paginationRef.current = tableData.pagination;
  }, [tableData.pagination]);

  const onSearch = useCallback(
    async (overrides) => {
      const values = form.getFieldsValue();
      const pagination = overrides?.pagination ?? paginationRef.current;

      const params = {
        start_date: values.date ? values.date[0].format("YYYY-MM-DD") : undefined,
        end_date: values.date ? values.date[1].format("YYYY-MM-DD") : undefined,
        reference: values.search,
        container: values.container,
        page: pagination.current,
        limit: pagination.pageSize,
        destination_delivery_id: data?.id,
      };

      const requestKey = JSON.stringify(params);
      const existing = pendingDestinationPackageContainerByKey.get(requestKey);
      if (existing) {
        return existing;
      }

      const promise = (async () => {
        try {
          setTableData((p) => ({ ...p, loading: true }));
          const responseData = await destinationPackageService.getDestinationPackageContainer(accessToken, params);
          const datasource = responseData.queryDestinationPackage.map((item) => ({
            ...item,
            input_total_received: item.total_received,
          }));
          setTableData((p) => ({
            ...p,
            loading: false,
            selectedRowKeys: [],
            datasource: datasource,
            total: responseData.totalCount,
          }));
        } catch (err) {
          console.error("[ModalAddPackageToDestination] onSearch: ", err);
          const { message } = settings.getErrorMessage(err);
          openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
          setTableData((p) => ({ ...p, loading: false }));
        } finally {
          pendingDestinationPackageContainerByKey.delete(requestKey);
        }
      })();

      pendingDestinationPackageContainerByKey.set(requestKey, promise);
      return promise;
    },
    [accessToken, data?.id, form]
  );

  useEffect(() => {
    if (!open || !accessToken || !data?.id) return;
    onSearch();
  }, [open, accessToken, data?.id, tableData.pagination.current, tableData.pagination.pageSize, onSearch]);

  const onClose = () => {
    setOpen(false);
  };

  const handleSearchSubmit = () => {
    const pageSize = tableData.pagination.pageSize;
    paginationRef.current = { current: 1, pageSize };
    setTableData((prev) => ({ ...prev, pagination: { ...prev.pagination, current: 1 } }));
    onSearch({ pagination: { current: 1, pageSize } });
  };

  const route = [
    { value: "land", label: tOps("Route.Land") },
    { value: "sea", label: tOps("Route.Sea") },
  ];

  const columns = [
    {
      title: t("Table_Total_Received"),
      dataIndex: "input_total_received",
      width: 150,
      render: (value, rec) => {
        return (
          <InputNumber
            value={value}
            min={0}
            max={rec.total_received}
            onChange={(value) => {
              const newData = { ...rec, input_total_received: value ?? 0 };
              const datasource = tableData.datasource.map((item) => (item.id === rec.id ? newData : item));
              setTableData((prev) => ({ ...prev, datasource }));
            }}
          />
        );
      },
    },
    {
      title: t("Table_Reference"),
      dataIndex: "reference",
      width: 150,
    },
    {
      title: t("Table_Tracking"),
      dataIndex: "tracking",
      width: 150,
    },
    {
      title: t("Table_Customer"),
      width: 150,
      render: (_, record) =>
        record.app_customer
          ? record.app_company?.company_code +
            "-" +
            record.app_customer.app_sale?.sale_code +
            record.app_customer.customer_code
          : "-",
    },
    {
      title: t("Table_Container"),
      dataIndex: "container",
      width: 170,
      render: (_, record) =>
        record.app_originpackage.app_originpackagetransportation.length > 0
          ? record.app_originpackage.app_originpackagetransportation[0].app_origintransportation.container
          : "-",
    },
    {
      title: t("Table_Route"),
      dataIndex: "route",
      width: 100,
      render: (_, record) => {
        const route = record.app_originpackage?.route;
        const label = getRouteLabel(route, i18n);
        return <p>{label}</p>;
      },
    },
    {
      title: t("Table_Origin"),
      dataIndex: "origin",
      width: 150,
      render: (_, record) => {
        const app_originpackage = record.app_originpackage;
        const app_warehouse =
          app_originpackage.app_warehouse_app_originpackage_origin_idToapp_warehouse?.warehouse_code || "-";
        return <p>{app_warehouse}</p>;
      },
    },
    {
      title: t("Table_Destination"),
      dataIndex: "destination",
      width: 150,
      render: (_, record) => record.app_warehouse?.warehouse_code || "-",
    },
  ];

  const onAddPackage = async () => {
    const selectedRow = tableData.datasource.filter((item) => tableData.selectedRowKeys.includes(item.id));
    if (selectedRow.length === 0) return;

    const destinationDeliveryId = data?.id || data?.destination_delivery_id;
    if (!destinationDeliveryId) {
      openNotificationWithIcon(
        settings.NotificationType.ERROR,
        <>{tMessage("Error_Message")}</>,
        <>{"Destination delivery id is missing. Please refresh and try again."}</>
      );
      return;
    }

    setSubmitting(true);

    let success = 0;
    let failed = 0;
    let responseSuccess = [];
    let responseFailed = [];
    let responseMessage = "";
    // Mirror the pattern used in ModalAddPackageToContainer
    let errorCode = [204, 400, 403, 500];

    try {
      for (let i = 0; i < selectedRow.length; i++) {
        const dataRow = selectedRow[i];
        const payload = {
          destination_package_id: dataRow.id,
          destination_delivery_id: destinationDeliveryId,
          total_received: dataRow.input_total_received,
        };
        try {
          const resp = await destinationPackageDeliveryService.createDestinationDeliveryPackage(accessToken, payload);
          if (resp === null || resp === undefined || errorCode.includes(resp?.status)) {
            failed += 1;
            responseFailed.push(dataRow.reference);
            responseMessage =
              resp?.response?.data?.message || resp?.message || "Failed to create destination package delivery";
          } else {
            success += 1;
            responseSuccess.push(dataRow.reference);
          }
        } catch (err) {
          failed += 1;
          responseFailed.push(dataRow.reference);
          const { message } = settings.getErrorMessage(err);
          responseMessage = message || "Failed to create destination package delivery";
        }
      }
      if (success > 0) {
        notiApi.success({
          message: tMessage("Success_Message"),
          description: tMessage("Success_Description"),
        });
        const pageSize = tableData.pagination.pageSize;
        paginationRef.current = { current: 1, pageSize };
        setTableData((prev) => ({ ...prev, pagination: { ...prev.pagination, current: 1 } }));
        setSubmitting(false);
        fetchPackage({ id: destinationDeliveryId, page: 1 });
        await onSearch({ pagination: { current: 1, pageSize } });
      }

      if (failed > 0) {
        const lowerCaseMsg = typeof responseMessage === "string" ? responseMessage.trim().toLowerCase() : "";
        if (lowerCaseMsg.includes("destination package already exists")) {
          openNotificationWithIcon(
            settings.NotificationType.ERROR,
            <>{tMessage("Error_Message")}</>,
            <>
              {tMessage("Error_Some_Already_Exist", {
                skippedCount: responseFailed.length,
                failedList: responseFailed.join(", "),
              })}
            </>
          );
        } else {
          openNotificationWithIcon(
            settings.NotificationType.ERROR,
            <>{tMessage("Error_Message")}</>,
            <>
              {tMessage("Error_Add_Package_Failed", {
                errorText: responseMessage,
                failedList: responseFailed.join(", "),
              })}
            </>
          );
        }
        setTimeout(() => {
          setSubmitting(false);
        }, 5000);
      }
    } catch (err) {
      console.error("[ModalAddPackageToDestination] onAddPackage: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{tMessage("Error_Message")}</>, <>{`${message}`}</>);
      setSubmitting(false);
    }
  };

  const handlePopupScroll = (e) => {
    const { target } = e;
    const { loading, hasMore } = containerData;
    if (target.scrollTop + target.offsetHeight === target.scrollHeight && !loading && hasMore) {
      setContainerData((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  };

  return (
    <>
      {contextHolder}
      <Modal open={open} onCancel={onClose} footer={null} title={t("Title")} width={"85%"} destroyOnClose>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <div className="border border-gray-200 rounded-md bg-white p-4 shadow-sm">
              <div className="font-semibold text-base mb-4">{t("Search_Title")}</div>
              <Form form={form} layout="vertical" onFinish={handleSearchSubmit}>
                <Row gutter={[16]}>
                  <Col span={8}>
                    <Form.Item label={t("Search_Label_Create_Date")} name={"date"}>
                      <DatePicker.RangePicker format={"YYYY-MM-DD"} className="w-full" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={t("Search_Label_Container")} name={"container"}>
                      <FetchingSelect />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={t("Search_Label_Search")} name={"search"}>
                      <Input placeholder="Reference/Tracking" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Button
                      type="primary"
                      htmlType="submit"
                    >
                      {tBtn("Search")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          </Col>
          <Col span={24}>
            <div className="border border-gray-200 rounded-md bg-white shadow-sm p-4 pt-5">
              <Row gutter={[0, 8]}>
                {hasPermission(permissions.DestinationPackageDelivery.ADD) && (
                  <Col span={24}>
                    <Button
                      type="primary"
                      disabled={isEmpty(tableData.selectedRowKeys)}
                      loading={submitting}
                      onClick={onAddPackage}
                    >
                      {t("BTN_Add_Package")}
                    </Button>
                  </Col>
                )}
                <Col span={24}>
                  <Table
                    rowKey={"id"}
                    loading={tableData.loading}
                    dataSource={tableData.datasource}
                    columns={columns}
                    rowSelection={{
                      selectedRowKeys: tableData.selectedRowKeys,
                      onChange: (selectedRowKeys) => setTableData((prev) => ({ ...prev, selectedRowKeys })),
                    }}
                    pagination={{
                      current: tableData.pagination.current,
                      pageSize: tableData.pagination.pageSize,
                      showSizeChanger: false,
                      total: tableData.total,
                      onChange: (current, pageSize) =>
                        setTableData((prev) => ({
                          ...prev,
                          pagination: { ...prev.pagination, current, pageSize },
                        })),
                    }}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ModalAddPackageToDestination;

const FetchingSelect = ({ value, onChange }) => {
  const PAGE_SIZE = 10;
  const { accessToken } = useAccessToken();

  const [containerList, setContainerList] = useState([]);
  const [apiParams, setApiParams] = useState({ page: 1, limit: PAGE_SIZE, container: undefined });
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchOptions = async () => {
    const requestKey = JSON.stringify(apiParams);
    const existing = pendingContainerListByKey.get(requestKey);
    if (existing) {
      return existing;
    }

    const promise = (async () => {
      setLoading(true);
      try {
        const resp = await destinationPackageDeliveryService.getContainerList(accessToken, apiParams);
        const respContainer = resp?.queryOriginTransportation?.map((item) => item.container) || [];
        const total = resp?.totalCount || 0;
        setContainerList((prev) => {
          const list =
            apiParams.page === 1 && apiParams.container ? respContainer : uniqueArray([...prev, ...respContainer]);
          return list;
        });

        if (apiParams.page * PAGE_SIZE >= total || respContainer.length < PAGE_SIZE) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      } finally {
        setLoading(false);
        pendingContainerListByKey.delete(requestKey);
      }
    })();

    pendingContainerListByKey.set(requestKey, promise);
    return promise;
  };

  useEffect(() => {
    fetchOptions();
  }, [apiParams]);

  const options = useMemo(() => {
    return containerList.map((item) => ({ label: item, value: item }));
  }, [containerList]);

  const handlePopupScroll = (e) => {
    const { target } = e;
    if (target.scrollTop + target.offsetHeight === target.scrollHeight && !loading && hasMore) {
      setApiParams((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const handleSearch = (inputValue) => {
    setApiParams((prev) => ({ ...prev, page: 1, container: inputValue }));
  };

  return (
    <Select
      allowClear
      value={value}
      onChange={onChange}
      showSearch
      onPopupScroll={handlePopupScroll}
      onSearch={handleSearch}
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : "No data"}
      options={options}
    />
  );
};
